import { call, put, takeLatest, select, take } from 'redux-saga/effects';
import { get } from 'lodash';
import { getUserContext, changeUserProjectRequest, acceptGdpr } from 'api/UserAPI';
import { impersonateUserRequest } from 'api/InternalAdminAPI';
import { addNegativeToast } from 'modules/toastContainer';
import { addToRootSaga } from 'store';
import { getRouterLocation } from './selectors';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { PROJECT_MEMBERS_PAGE_URL_REGEXP } from './constants';
import { getRedirectDestination, shouldNavigateBackToIndex } from './utils';

export const getErrorMessage = (error, defaultMessage) => {
  if (error.response && error.response.statusText) {
    return error.response.statusText;
  }
  return defaultMessage;
};

export function* fetchUserContext() {
  try {
    const response = yield call(getUserContext);
    yield put(actions.fetchUserProjectResponse(response.data));
    if (window.document)
      window.document.dispatchEvent(
        new CustomEvent('userContextLoaded', { detail: response.data }),
      );
  } catch (error) {
    // If the user receives a 'Unauthorized' response, reload the page to force a login
    if (get(error, 'response.status') === 401) {
      window.location.reload();
    }
  }
}

export function* changeUserProjectSaga({ projectId, onComplete, reloadPageOnComplete = true }) {
  try {
    const { pathname, search } = yield select(getRouterLocation);
    yield call(changeUserProjectRequest, projectId);

    if (onComplete) {
      onComplete();
    }

    if (reloadPageOnComplete) {
      // if currently in project members page
      if (PROJECT_MEMBERS_PAGE_URL_REGEXP.test(pathname)) {
        // navigate to the newly selected project's members
        window.location.assign(`/projects/${projectId}`);
      } else if (shouldNavigateBackToIndex(pathname)) {
        try {
          const rerouteUrl = getRedirectDestination(pathname, search);
          window.location.assign(rerouteUrl);
        } catch (e) {
          // an unexpected error would reload the root page
          window.location.assign('/');
        }
      } else if (pathname.includes('/messagingInsights/views')) {
        window.location.assign('/messagingInsights');
      } else {
        // clear the query params and reload the app - refresh required to reset app state
        const currentUrl = new URL(window.location.href);
        const nextSearchParams = new URLSearchParams();
        nextSearchParams.set('projectId', projectId);
        if (currentUrl.searchParams.has('campaignId')) {
          nextSearchParams.set('campaignId', currentUrl.searchParams.get('campaignId'));
        }
        window.location.search = nextSearchParams.toString();
      }
    }
  } catch (error) {
    const msg = getErrorMessage(error, 'Unable to change projects. Please try again.');
    yield put(addNegativeToast(msg));
  }
}

export function* changeUserProjectAndJourneySaga({ projectId, workflowId, workflowType }) {
  try {
    yield call(changeUserProjectRequest, projectId);
    const journeyUrl = new URL(
      `/workflows/${workflowId}/edit?workflowType=${workflowType}`,
      window.location.origin,
    );
    window.location.assign(journeyUrl.href);
  } catch (error) {
    const msg = getErrorMessage(error, 'Unable to change journeys');
    yield put(addNegativeToast(msg));
  }
}
export function* saveAndSwitchSaga({ saveWorkflowAction, projectId, workflowId, workflowType }) {
  try {
    yield put(saveWorkflowAction);
    // action occurs at end of saveWorkflowDraftSaga, which is the only one that can be saved.
    // Importing that saga or action in this file causes the app to crash
    yield take('workflows/SAVE_WORKFLOW_DRAFT_RESPONSE');
    yield changeUserProjectAndJourneySaga({ projectId, workflowId, workflowType });
  } catch (error) {
    const msg = getErrorMessage(error, 'Unable to change journeys');
    yield addNegativeToast(msg);
  }
}

export function* acceptGdprSaga() {
  try {
    yield call(acceptGdpr);
    yield put(actions.fetchUserProjectRequest());
  } catch (error) {
    const msg = getErrorMessage(error, 'Please try again later');
    yield put(addNegativeToast(msg));
  }
}

export function* viewAsUserSaga({ email }) {
  try {
    yield call(impersonateUserRequest, email);
    yield put(actions.viewAsUserResponse());
    window.location.reload();
  } catch (error) {
    const msg = getErrorMessage(error, 'Unable to complete the request at this time');
    yield put(addNegativeToast(msg));
  }
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_USER_CONTEXT_REQUEST, fetchUserContext),
  takeLatest(actionTypes.CHANGE_USER_PROJECT_REQUEST, changeUserProjectSaga),
  takeLatest(actionTypes.CHANGE_USER_PROJECT_AND_JOURNEY_REQUEST, changeUserProjectAndJourneySaga),
  takeLatest(actionTypes.SAVE_AND_SWITCH_REQUEST, saveAndSwitchSaga),
  takeLatest(actionTypes.ACCEPT_GDPR_REQUEST, acceptGdprSaga),
  takeLatest(actionTypes.VIEW_AS_USER_REQUEST, viewAsUserSaga),
]);
